export const getQueryParam = (n, s = "") => s ? new URLSearchParams(s.replace(/\?/g, (m, o) => o === 0 ? "?" : "&")).get(n) : null;

const getAtVal = (p = {}, n) => (p.custom_attributes || []).find(a => a.name === n)?.value || p[n] || p?.metadata?.[n] || null;

export const optimizeImageUrl = (url, width, quality) => {
  if (!url || typeof url !== "string") return url;
  try {
    const u = new URL(url);
    if (width) u.searchParams.set("w", width);
    if (quality) u.searchParams.set("q", quality);
    u.searchParams.set("auto", "format,compress");
    return u.toString();
  } catch (e) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}w=${width}${quality ? `&q=${quality}` : ""}&auto=format,compress`;
  }
};

export const fetchCampaignData = (id, signal) => {
  const fJ = u => fetch(u, { signal }).then(r => r.ok ? r.json() : Promise.reject(r.status));
  const sH = window?.sketches?.config?.["sketches-host"], pB = typeof sH === "string" ? sH.replace(/\/$/, "") : null;
  const pU = pB ? `${pB}/${encodeURIComponent(id)}` : `${window.location.origin}/${encodeURIComponent(id)}`;
  return fJ(pU).catch(() => fJ(`${window.location.origin}/${encodeURIComponent(id)}`)).then(p => {
    if (!p?.heading_text && !p?.hero_section?.heading_text && !p?.theme?.heading_text) throw new Error("Invalid");
    return p;
  });
};

export const normalizeSubscriptionCampaign = (p = {}, sG = [], cid = null) => {
  if (!p) return { heroSection: {}, planProps: { plans: [], colors: {} }, whyChooseProps: {} };
  const darkLogo = "https://images.assettype.com/deccanherald/2026-05-09/tlcg3dbz/logo-dark.png", lightLogo = "https://images.assettype.com/deccanherald/2026-05-09/74k4yppj/e-paper.png";
  const h = {
    heading_text: p.heading_text || p.hero_section?.heading_text,
    subheading_text: p.subheading_text || p.hero_section?.subheading_text,
    background_desktop: optimizeImageUrl(p.image_dsktp_bg || p.hero_section?.background_desktop || p.desktop_bg, 1440, 60),
    background_mobile: optimizeImageUrl(p.image_mobile_bg || p.hero_section?.background_mobile || p.mobile_bg, 800, 60),
    image_logo: optimizeImageUrl(p.image_logo || p.hero_section?.image_logo || (String(p.darkmode_epaper_logo) === "true" ? darkLogo : lightLogo), 400),
    heading_color: p.text_color,
    subheading_color: p.text_color
  };
  let allAt = sG?.flatMap(g => g.subscription_plans || g.plans || []) || [];
  if (cid) allAt = allAt.filter(pl => (pl.custom_attributes || []).some(a => a.name === "campaign_id" && a.value === cid));
  const c = { palette1: p.palette1, palette2: p.palette2, palette3: p.palette3, palette4: p.palette4, text_color: p.text_color, cta_text_color: p.cta_text_color };
  const gCTA = p.cta_text || p.button_text || "Subscribe";
  const merged = (p.plans || (allAt.length ? allAt : [])).map(pl => {
    const m = allAt.find(at => (pl.id && String(at.id) === String(pl.id)) || (pl.plan_id && at.plan_id === pl.plan_id)) || pl;
    const attrs = m.custom_attributes || [];
    return { ...m, ...pl, cta_text: pl.cta_text || pl.button_text || m.cta_text || m.button_text || gCTA, price: m.price_cents ? m.price_cents / 100 : m.price, is_popular: getIsPopularPlan(m), tagline: getPlanTagline(m), plan_features: getPlanFeatures(m) };
  }).sort((a, b) => parseInt(getAtVal(a, "plan_order") || 999) - parseInt(getAtVal(b, "plan_order") || 999));

  return { heroSection: h, planProps: { plans: merged, planCard: { palette4: c.palette4, text_color: c.text_color }, colors: c }, whyChooseProps: { colors: c, subscriberCount: { count: p.subscriber_count || p.subscriber_section?.count, label: p.subscriber_label || p.subscriber_section?.label } } };
};

export const getPlanTitle = p => p.title || p.plan_title || p.name || "";
export const getPlanPrice = p => p.price || p.plan_price || p.amount || "";
export const getPlanOriginalPrice = p => getAtVal(p, "mrp_inr") || "";
export const getPlanDiscountText = p => {
  const v = getAtVal(p, "save_price_inr") || "";
  return v ? `Save ₹${v}` : "";
};

export const getPlanDuration = p => {
  const d = p.duration || p.plan_duration || p.billing_period;
  if (d) return `/ ${d}`;
  const u = String(p.duration_unit || "").toLowerCase();
  return u ? `/ ${u === "month" ? "Month" : u === "year" ? "Year" : u}` : "";
};

export const getPlanTagline = p => {
  let t = getAtVal(p, "tagline") || getAtVal(p, "plan_tagline") || "";
  return (typeof t === "string" ? t : "").replace(/<[^>]*>/g, "").trim();
};
export const getIsPopularPlan = p => {
  const v = getAtVal(p, "meta_chip") || p.meta_chip || p.metadata?.meta_chip;
  if (!v || v === "no" || v === "false" || v === false) return null;
  return v;
};

export const getPlanFeatures = p => {
  let f = (p.custom_attributes || []).find(a => a.name?.toLowerCase() === "description")?.value || "";
  if (typeof f === "string") {
    if (f.includes("<li")) return Array.from(f.matchAll(/<li[^>]*>(.*?)<\/li>/gis), x => x[1].replace(/<[^>]*>/g, "").trim());
    return f.split(/(?:<\/p>|<br\s*\/?>|\n|,)/i).map(x => x.replace(/<[^>]*>/g, "").trim()).filter(Boolean);
  }
  return Array.isArray(f) ? f.map(x => (typeof x === "string" ? x : (x?.text || x?.feature_text || x?.label || "")).replace(/<[^>]*>/g, "").trim()).filter(Boolean) : [];
};

export const getPlanButtonText = p => p.cta_text || p.button_text || p.plan_cta_text || "Subscribe";

export const applyPlanSectionStyles = (node, pc = {}, c = {}) => {
  if (!node) return;
  const q = (s, f) => node.querySelectorAll(s).forEach(f);
  q("[data-plan-card]", n => { if (pc.palette4) n.style.backgroundColor = pc.palette4; if (c.palette1) n.style.borderColor = c.palette1; });
  q("[data-popular-badge]", n => { if (c.palette2) n.style.backgroundColor = c.palette2; if (c.text_color) n.style.color = c.text_color; });
  q("[data-plan-title], [data-plan-price], [data-plan-duration]", n => { if (c.text_color) n.style.color = c.text_color; });
  q("[data-plan-discount-text]", n => { if (c.palette2) n.style.color = c.palette2; });
  q("[data-plan-tagline]", n => { if (c.palette1) n.style.color = c.palette1; });
  q("[data-feature-text]", n => { if (c.text_color) n.style.color = c.text_color; });
  q("[data-plan-button]", n => { if (c.palette1) n.style.backgroundColor = c.palette1; if (c.cta_text_color) n.style.color = c.cta_text_color; });
  q("[data-tick-icon-path]", n => { if (c.palette2) n.setAttribute("fill", c.palette2); });
};
